// Textes trop larges pour la scène : on RÉDUIT leur taille jusqu'à ce qu'ils
// tiennent (un calcul « E(A) = 40 × 9,8 × 10 = 3920 J » coupé des deux côtés
// sur un téléphone). Pur : mesure sans navigateur (`measure.ts`), sur toute
// la vie de l'objet (formule qui se transforme, texte changé, nombre qui
// grandit). Appliqué à la création par l'IA, et par l'admin sur un brouillon ;
// le lecteur réduit aussi à l'affichage (filet de sécurité, polices réelles).

import { sampleTimes, sceneLayout } from './layout';
import { migrateJraf } from './migrate';
import { jrafDocumentSchema } from './schema/document';
import { compileAnimation, type CompiledAnimation } from './timeline';

/** Marge laissée libre de chaque côté de la scène (px du canevas). */
export const FIT_MARGIN = 8;
/** On ne descend pas sous cette taille (px) : au-delà, il faut couper le texte. */
export const FIT_MIN_SIZE = 12;
/** Sous cette largeur disponible, l'objet est collé au bord : le déplacer, pas le réduire. */
const FIT_MIN_ROOM = 40;

export interface TextFit {
  objectId: string;
  from: number;
  to: number;
}

/**
 * Largeur dont dispose un texte ancré en `x` (aligné au début, au milieu
 * ou à la fin) sans sortir de la scène.
 */
export function roomAt(
  x: number,
  align: 'start' | 'middle' | 'end',
  width: number,
  margin = FIT_MARGIN,
): number {
  if (align === 'start') return width - margin - x;
  if (align === 'end') return x - margin;
  return 2 * Math.min(x - margin, width - margin - x);
}

/** Tailles qui font tenir les textes, formules et nombres trop larges. */
export function textFits(anim: CompiledAnimation): TextFit[] {
  const W = anim.doc.canvas.width;
  const byId = new Map(anim.objects.map((o) => [o.id, o]));
  const factor = new Map<string, number>();
  for (const scene of anim.scenes)
    for (const t of sampleTimes(scene))
      for (const it of sceneLayout(anim, scene, t)) {
        if (!it.text || it.role) continue;
        const o = byId.get(it.id);
        if (!o || (o.type !== 'text' && o.type !== 'formula' && o.type !== 'value')) continue;
        if (o.type === 'text' && o.maxWidth !== undefined) continue; // coupé en lignes
        const r = it.text.ink;
        const width = r.x1 - r.x0;
        const anchor = o.align === 'start' ? r.x0 : o.align === 'end' ? r.x1 : (r.x0 + r.x1) / 2;
        const room = roomAt(anchor, o.align, W);
        if (width <= room + 0.5 || room < FIT_MIN_ROOM || width <= 0) continue;
        factor.set(o.id, Math.min(factor.get(o.id) ?? 1, room / width));
      }
  const out: TextFit[] = [];
  for (const [id, f] of factor) {
    const o = byId.get(id)!;
    if (o.type !== 'text' && o.type !== 'formula' && o.type !== 'value') continue;
    // arrondi au demi-pixel inférieur : tient à coup sûr
    const to = Math.max(FIT_MIN_SIZE, Math.floor(o.size * f * 2) / 2);
    if (to < o.size) out.push({ objectId: id, from: o.size, to });
  }
  return out.sort((a, b) => a.objectId.localeCompare(b.objectId));
}

/**
 * Brouillon BRUT (tel qu'en base) dont les textes trop larges sont réduits.
 * Document illisible : rendu tel quel, sans changement (la validation le dira).
 */
export function fitTextsToCanvas(raw: unknown): { doc: unknown; fits: TextFit[] } {
  const migrated = migrateJraf(raw);
  if (!migrated.ok) return { doc: raw, fits: [] };
  const parsed = jrafDocumentSchema.safeParse(migrated.doc);
  if (!parsed.success) return { doc: raw, fits: [] };
  let fits: TextFit[];
  try {
    fits = textFits(compileAnimation(parsed.data));
  } catch {
    return { doc: raw, fits: [] };
  }
  if (fits.length === 0) return { doc: raw, fits };
  const doc = structuredClone(raw) as { objects?: unknown };
  const sizes = new Map(fits.map((f) => [f.objectId, f.to]));
  if (Array.isArray(doc.objects))
    for (const o of doc.objects)
      if (o && typeof o === 'object' && sizes.has((o as { id?: string }).id ?? ''))
        (o as { size?: number }).size = sizes.get((o as { id: string }).id);
  return { doc, fits };
}
